import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { 
    Brain, 
    TrendingUp, 
    Target,
    Mic,
    MessageSquare,
    Trophy,
    BookOpen,
    Lightbulb,
    ArrowRight,
    Play,
    ChevronUp,
    ChevronDown,
    Minus,
    Volume2,
    Loader2,
    Save,
    Square
} from 'lucide-react';
import SpeechAnalyticsHistory from '../components/ai/SpeechAnalyticsHistory';

const LearningProfile = () => {
    const { api, user } = useAuth();
    const [profile, setProfile] = useState(null);
    const [recommendations, setRecommendations] = useState(null);
    const [nextActivity, setNextActivity] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // AI Preferences state
    const [userPrefs, setUserPrefs] = useState(null);
    const [availableVoices, setAvailableVoices] = useState(null);
    const [prefsSaving, setPrefsSaving] = useState(false);
    const [previewingVoice, setPreviewingVoice] = useState(null);
    const previewAudioRef = useRef(null);

    useEffect(() => {
        fetchData();
        fetchUserPreferences();
        
        // Cleanup audio on unmount
        return () => {
            if (previewAudioRef.current) {
                previewAudioRef.current.pause();
                previewAudioRef.current = null;
            }
        };
    }, []);
    
    const fetchUserPreferences = async () => {
        try {
            const response = await api.get('/user/preferences');
            setUserPrefs(response.data.preferences);
            setAvailableVoices(response.data.available_voices);
        } catch (error) {
            console.error('Failed to load user preferences:', error);
        }
    };
    
    const previewVoice = async (voiceId, voiceName) => {
        // Stop any currently playing preview
        if (previewAudioRef.current) {
            previewAudioRef.current.pause();
            previewAudioRef.current = null;
        }
        
        if (previewingVoice === voiceId) {
            // Clicking same voice stops it
            setPreviewingVoice(null);
            return;
        }
        
        setPreviewingVoice(voiceId);
        
        try {
            // Use query parameters instead of body
            const previewText = encodeURIComponent(`Hello! I'm ${voiceName}. I'll be your AI training assistant.`);
            const response = await api.post(`/user/preferences/preview-voice?voice_id=${voiceId}&text=${previewText}`);
            
            if (response.data.audio_url) {
                const audio = new Audio(response.data.audio_url);
                previewAudioRef.current = audio;
                
                audio.onended = () => {
                    setPreviewingVoice(null);
                    previewAudioRef.current = null;
                };
                
                audio.onerror = () => {
                    setPreviewingVoice(null);
                    previewAudioRef.current = null;
                    toast.error('Failed to play voice preview');
                };
                
                audio.play();
            }
        } catch (error) {
            console.error('Failed to preview voice:', error);
            toast.error('Failed to preview voice');
            setPreviewingVoice(null);
        }
    };
    
    const saveUserPreferences = async () => {
        setPrefsSaving(true);
        try {
            const params = new URLSearchParams();
            // Always send ai_name even if empty (allows clearing)
            params.append('ai_name', userPrefs?.ai_name || '');
            if (userPrefs?.voice_id) params.append('voice_id', userPrefs.voice_id);
            if (userPrefs?.voice_language) params.append('voice_language', userPrefs.voice_language);
            
            const response = await api.put(`/user/preferences?${params.toString()}`);
            setUserPrefs(response.data.preferences);
            
            // Emit a custom event to notify other components (like RPATLesson) to refresh audio
            window.dispatchEvent(new CustomEvent('voicePreferencesChanged', { 
                detail: response.data.preferences 
            }));
            
            toast.success('AI preferences saved! Changes will apply to new audio.');
        } catch (error) {
            toast.error('Failed to save preferences');
        } finally {
            setPrefsSaving(false);
        }
    };

    const fetchData = async () => {
        try {
            const [profileRes, recsRes, nextRes] = await Promise.all([
                api.get('/learning-profile'),
                api.get('/learning-profile/recommendations'),
                api.get('/adaptive/next-activity')
            ]);
            setProfile(profileRes.data);
            setRecommendations(recsRes.data);
            setNextActivity(nextRes.data);
        } catch (error) {
            console.error('Failed to fetch profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const getTrendIcon = (trend) => {
        if (!trend || trend.length < 2) return <Minus className="h-4 w-4 text-muted-foreground" />;
        const recent = trend.slice(-5);
        const avgRecent = recent.reduce((a, b) => a + (b.score || 0), 0) / recent.length;
        const older = trend.slice(-10, -5);
        const avgOlder = older.length > 0 ? older.reduce((a, b) => a + (b.score || 0), 0) / older.length : avgRecent;
        
        if (avgRecent > avgOlder + 5) return <ChevronUp className="h-4 w-4 text-emerald-500" />;
        if (avgRecent < avgOlder - 5) return <ChevronDown className="h-4 w-4 text-red-500" />;
        return <Minus className="h-4 w-4 text-muted-foreground" />;
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-emerald-500';
        if (score >= 60) return 'text-yellow-500';
        return 'text-red-500';
    };

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="learning-profile-page">
                <div>
                    <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                        <Brain className="h-8 w-8 text-primary" />
                        Learning Profile
                    </h1>
                    <p className="text-muted-foreground mt-1">Your personalized learning journey</p>
                </div>

                {/* Next Recommended Activity */}
                {nextActivity && (
                    <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
                        <CardContent className="py-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 rounded-xl bg-primary/20">
                                        <Lightbulb className="h-6 w-6 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-lg">Recommended Next Activity</h3>
                                        <p className="text-sm text-muted-foreground">{nextActivity.reason}</p>
                                    </div>
                                </div>
                                <Link to={nextActivity.activity_type === 'training' ? '/training' : 
                                          nextActivity.activity_type === 'simulation' ? '/simulation' : '/assessments'}>
                                    <Button className="btn-primary">
                                        <Play className="h-4 w-4 mr-2" />
                                        Start {nextActivity.activity_type}
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                )}

                <div className="grid gap-6 lg:grid-cols-3">
                    {/* Overview Stats */}
                    <Card className="lg:col-span-2">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <TrendingUp className="h-5 w-5 text-primary" />
                                Performance Overview
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="text-center p-4 rounded-xl bg-muted/50">
                                    <p className={`text-3xl font-bold ${getScoreColor(profile?.average_score || 0)}`}>
                                        {profile?.average_score || 0}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1">Average Score</p>
                                </div>
                                <div className="text-center p-4 rounded-xl bg-muted/50">
                                    <p className="text-3xl font-bold">{profile?.completed_topics || 0}</p>
                                    <p className="text-xs text-muted-foreground mt-1">Topics Completed</p>
                                </div>
                                <div className="text-center p-4 rounded-xl bg-muted/50">
                                    <p className="text-3xl font-bold">{profile?.completed_simulations || 0}</p>
                                    <p className="text-xs text-muted-foreground mt-1">Simulations</p>
                                </div>
                                <div className="text-center p-4 rounded-xl bg-muted/50">
                                    <p className="text-3xl font-bold">{profile?.total_training_time_hours || 0}h</p>
                                    <p className="text-xs text-muted-foreground mt-1">Training Time</p>
                                </div>
                            </div>

                            <Separator />

                            {/* Voice Profile */}
                            <div>
                                <h4 className="font-semibold flex items-center gap-2 mb-4">
                                    <Mic className="h-4 w-4 text-primary" />
                                    Voice Profile
                                </h4>
                                <div className="space-y-3">
                                    {profile?.voice_profile && Object.keys(profile.voice_profile).length > 0 ? (
                                        Object.entries(profile.voice_profile).map(([key, value]) => (
                                            <div key={key} className="space-y-1">
                                                <div className="flex justify-between text-sm">
                                                    <span className="capitalize">{key.replace('_', ' ')}</span>
                                                    <span className={`font-medium ${getScoreColor(value)}`}>{value}</span>
                                                </div>
                                                <Progress value={value} className="h-2" />
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-sm text-muted-foreground text-center py-4">
                                            Complete voice training to build your profile
                                        </p>
                                    )}
                                </div>
                            </div>

                            <Separator />

                            {/* Communication Profile */}
                            <div>
                                <h4 className="font-semibold flex items-center gap-2 mb-4">
                                    <MessageSquare className="h-4 w-4 text-secondary" />
                                    Communication Profile
                                </h4>
                                <div className="space-y-3">
                                    {profile?.communication_profile && Object.keys(profile.communication_profile).length > 0 ? (
                                        Object.entries(profile.communication_profile).map(([key, value]) => (
                                            <div key={key} className="space-y-1">
                                                <div className="flex justify-between text-sm">
                                                    <span className="capitalize">{key}</span>
                                                    <span className={`font-medium ${getScoreColor(value)}`}>{value}</span>
                                                </div>
                                                <Progress value={value} className="h-2" />
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-sm text-muted-foreground text-center py-4">
                                            Complete communication training to build your profile
                                        </p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Strengths & Weaknesses */}
                    <div className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Trophy className="h-5 w-5 text-emerald-500" />
                                    Strengths
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {profile?.strengths?.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {profile.strengths.map((s, i) => (
                                            <Badge key={i} className="bg-emerald-500/10 text-emerald-500">
                                                {s}
                                            </Badge>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted-foreground">
                                        Complete more training to identify strengths
                                    </p>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Target className="h-5 w-5 text-yellow-500" />
                                    Areas to Improve
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {profile?.weaknesses?.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {profile.weaknesses.map((w, i) => (
                                            <Badge key={i} className="bg-yellow-500/10 text-yellow-500">
                                                {w}
                                            </Badge>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted-foreground">
                                        Great job! No significant weak areas identified
                                    </p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Improvement Trend */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <TrendingUp className="h-5 w-5 text-primary" />
                                    Progress Trend
                                    {getTrendIcon(profile?.improvement_trend)}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {profile?.improvement_trend?.length > 0 ? (
                                    <div className="space-y-2">
                                        {profile.improvement_trend.slice(-5).map((t, i) => (
                                            <div key={i} className="flex items-center justify-between text-sm">
                                                <span className="capitalize text-muted-foreground">{t.activity}</span>
                                                <span className={`font-medium ${getScoreColor(t.score)}`}>{t.score}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted-foreground">
                                        Start training to see your progress
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>

                {/* Recommendations */}
                {recommendations && (
                    <div className="grid gap-6 lg:grid-cols-2">
                        {/* Recommended Topics */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <BookOpen className="h-5 w-5 text-primary" />
                                    Recommended Topics
                                </CardTitle>
                                <CardDescription>
                                    Personalized suggestions based on your performance
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {recommendations.recommended_topics?.length > 0 ? (
                                    <div className="space-y-3">
                                        {recommendations.recommended_topics.map((topic, i) => (
                                            <div 
                                                key={i} 
                                                className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 rounded-lg bg-primary/10">
                                                        <BookOpen className="h-4 w-4 text-primary" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">{topic.name}</p>
                                                        <p className="text-xs text-muted-foreground capitalize">{topic.difficulty}</p>
                                                    </div>
                                                </div>
                                                {topic.is_weak_area && (
                                                    <Badge className="bg-yellow-500/10 text-yellow-500">
                                                        Focus Area
                                                    </Badge>
                                                )}
                                            </div>
                                        ))}
                                        <Link to="/training">
                                            <Button variant="outline" className="w-full mt-2">
                                                Start Training
                                                <ArrowRight className="h-4 w-4 ml-2" />
                                            </Button>
                                        </Link>
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted-foreground text-center py-4">
                                        No specific recommendations yet
                                    </p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Recommended Simulations */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Mic className="h-5 w-5 text-secondary" />
                                    Recommended Simulations
                                </CardTitle>
                                <CardDescription>
                                    Practice scenarios to improve your skills
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {recommendations.recommended_simulations?.length > 0 ? (
                                    <div className="space-y-3">
                                        {recommendations.recommended_simulations.map((sim, i) => (
                                            <div 
                                                key={i} 
                                                className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 rounded-lg bg-secondary/10">
                                                        <Mic className="h-4 w-4 text-secondary" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium capitalize">
                                                            {sim.type.replace('_', ' ')}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground">{sim.reason}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        <Link to="/simulation">
                                            <Button variant="outline" className="w-full mt-2">
                                                Start Simulation
                                                <ArrowRight className="h-4 w-4 ml-2" />
                                            </Button>
                                        </Link>
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted-foreground text-center py-4">
                                        No specific recommendations yet
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Speech Analytics History - Full View */}
                <SpeechAnalyticsHistory compact={false} />
                
                {/* AI Voice Preferences Section - Available to all users */}
                <Card className="mt-6">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Volume2 className="h-5 w-5 text-primary" />
                            AI Assistant Customization
                        </CardTitle>
                        <CardDescription>Personalize your AI trainer&apos;s voice and name</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {!userPrefs ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : (
                            <>
                                {/* AI Name */}
                                <div className="space-y-2">
                                    <Label>AI Assistant Name</Label>
                                    <Input
                                        value={userPrefs?.ai_name || 'Alex'}
                                        onChange={(e) => setUserPrefs({ ...userPrefs, ai_name: e.target.value })}
                                        placeholder="Enter a name for your AI assistant"
                                        maxLength={20}
                                        data-testid="ai-name-input-profile"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Give your AI trainer a personalized name (max 20 characters)
                                    </p>
                                </div>
                                
                                {/* Voice Language */}
                                <div className="space-y-2">
                                    <Label>Voice Accent</Label>
                                    <Select
                                        value={userPrefs?.voice_language || 'en-us'}
                                        onValueChange={(value) => {
                                            setUserPrefs({ ...userPrefs, voice_language: value, voice_id: null });
                                        }}
                                    >
                                        <SelectTrigger data-testid="voice-language-select-profile">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="en-us">🇺🇸 American English</SelectItem>
                                            <SelectItem value="en-gb">🇬🇧 British English</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                {/* Voice Selection */}
                                {availableVoices && (
                                    <div className="space-y-4">
                                        <Label>Voice Selection</Label>
                                        
                                        {/* Female Voices */}
                                        <div className="space-y-2">
                                            <span className="text-sm font-medium text-muted-foreground">Female Voices</span>
                                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                                                {availableVoices[userPrefs?.voice_language || 'en-us']?.female?.map((voice) => (
                                                    <div
                                                        key={voice.id}
                                                        className={`p-3 rounded-lg border text-left transition-all ${
                                                            userPrefs?.voice_id === voice.id 
                                                                ? 'border-primary bg-primary/10' 
                                                                : 'border-muted hover:border-primary/50'
                                                        }`}
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={() => setUserPrefs({ ...userPrefs, voice_id: voice.id })}
                                                            className="w-full text-left"
                                                            data-testid={`voice-profile-${voice.id}`}
                                                        >
                                                            <div className="font-medium text-sm">{voice.name}</div>
                                                            <div className="text-xs text-muted-foreground truncate">{voice.description}</div>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                previewVoice(voice.id, voice.name);
                                                            }}
                                                            className="mt-2 w-full flex items-center justify-center gap-1 text-xs text-primary hover:text-primary/80"
                                                            data-testid={`preview-${voice.id}`}
                                                        >
                                                            {previewingVoice === voice.id ? (
                                                                <><Square className="h-3 w-3" /> Stop</>
                                                            ) : (
                                                                <><Play className="h-3 w-3" /> Preview</>
                                                            )}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        
                                        {/* Male Voices */}
                                        <div className="space-y-2">
                                            <span className="text-sm font-medium text-muted-foreground">Male Voices</span>
                                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                                                {availableVoices[userPrefs?.voice_language || 'en-us']?.male?.map((voice) => (
                                                    <div
                                                        key={voice.id}
                                                        className={`p-3 rounded-lg border text-left transition-all ${
                                                            userPrefs?.voice_id === voice.id 
                                                                ? 'border-primary bg-primary/10' 
                                                                : 'border-muted hover:border-primary/50'
                                                        }`}
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={() => setUserPrefs({ ...userPrefs, voice_id: voice.id })}
                                                            className="w-full text-left"
                                                            data-testid={`voice-profile-${voice.id}`}
                                                        >
                                                            <div className="font-medium text-sm">{voice.name}</div>
                                                            <div className="text-xs text-muted-foreground truncate">{voice.description}</div>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                previewVoice(voice.id, voice.name);
                                                            }}
                                                            className="mt-2 w-full flex items-center justify-center gap-1 text-xs text-primary hover:text-primary/80"
                                                            data-testid={`preview-${voice.id}`}
                                                        >
                                                            {previewingVoice === voice.id ? (
                                                                <><Square className="h-3 w-3" /> Stop</>
                                                            ) : (
                                                                <><Play className="h-3 w-3" /> Preview</>
                                                            )}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Save Button */}
                                <Button 
                                    onClick={saveUserPreferences}
                                    disabled={prefsSaving}
                                    className="w-full"
                                    data-testid="save-ai-prefs-profile"
                                >
                                    {prefsSaving ? (
                                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                                    ) : (
                                        <><Save className="h-4 w-4 mr-2" /> Save AI Preferences</>
                                    )}
                                </Button>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>
        </MainLayout>
    );
};

export default LearningProfile;
